/**
 * Teaching the Money In list what is not a customer payment.
 *
 * The Mercury account receives everything: customer payments, Mercury's own
 * monthly cashback, penny deposits from Wells Fargo and Intuit proving they own
 * the account, and the owners moving their own money in. The list showed all of
 * it and asked a person about each one, which is right on the day it was built
 * and tiresome by the second month.
 *
 * Jonathon, 2026-09-06: "i want it to monitor what the rep selects and teach
 * itself."
 *
 * ## How it learns
 *
 * From setting aside, and only from setting aside. Set the same payer aside
 * twice and Roof Board stops asking about them.
 *
 * It deliberately does **not** learn to match. Matching money to a job ticks a
 * payment box, takes the job off the ongoing board and can move it to complete,
 * so a wrong automatic match is money the company stops chasing while it is
 * still owed. Setting aside is the recoverable direction: the row is still
 * there, it says why, and one click puts it back. Everything here is built on
 * that asymmetry.
 *
 * ## The safety valve
 *
 * A learned payer is not enough on its own. **An amount that matches what some
 * job is waiting for always goes to a person**, however familiar the payer is.
 *
 * That is what stops the one bad outcome. If a rep sets a real customer aside
 * by mistake and the rule is learned from it, the next payment from that
 * customer would vanish — except that it looks exactly like a payment a job is
 * waiting for, so it stops here and somebody sees it.
 */

import { formatMoney, type Cents, cents } from "./money.js";
import { feeTolerance } from "./paymentReceipt.js";

/**
 * How many times a payer must be set aside before Roof Board stops asking.
 *
 * Two. One is an accident or a one-off; two is a habit, and the person has by
 * then typed a reason twice. Higher would mean a month of Mercury cashback
 * before it settles down.
 */
export const RULE_EARNED_AFTER = 2;

/**
 * The payer, as a key.
 *
 * Bank lines arrive with inconsistent case and spacing — "SAMUEL SANDOVAL
 * ARROYO" one week and "Samuel Sandoval Arroyo" the next — and the description
 * beside them changes every time ("Samuel's Contribution", then nothing). So
 * the key is the counterparty alone, flattened. The description is what a
 * person reads, never what a rule matches on.
 */
export function normaliseCounterparty(
  value: string | null | undefined,
): string {
  return (
    (value ?? "")
      .toLowerCase()
      // `\u00a0` as well as ordinary spaces: bank feeds are full of non-breaking
      // spaces, and two names that differ only by one are the same payer.
      .replace(/[\s\u00a0]+/g, " ")
      .replace(/[.,;:·]+$/g, "")
      .trim()
  );
}

export interface DepositRule {
  /** The normalised counterparty this rule is about. */
  counterparty: string;
  /** How many times a person has set this payer aside. */
  timesSetAside: number;
  /** The last reason somebody gave, shown wherever the rule is. */
  reason: string;
}

/** Whether this payer has been set aside often enough to stop asking. */
export function ruleIsEarned(rule: DepositRule | null | undefined): boolean {
  return (rule?.timesSetAside ?? 0) >= RULE_EARNED_AFTER;
}

/** The rule for a payer, or null. */
export function ruleFor(
  counterparty: string | null | undefined,
  rules: readonly DepositRule[],
): DepositRule | null {
  const key = normaliseCounterparty(counterparty);
  if (!key) return null;
  return rules.find((rule) => rule.counterparty === key) ?? null;
}

/**
 * Whether an amount could be a payment some job is waiting for.
 *
 * Compared with the same tolerance the matcher uses, because a card payment
 * reaches the bank about 3% light and a bank transfer arrives whole. Anything
 * inside that band is somebody's roof money until a person says otherwise.
 */
export function looksLikeAPayment(
  amount: Cents,
  awaited: readonly Cents[],
): boolean {
  const received = cents(amount);
  return awaited.some((expected) => {
    if (expected <= 0) return false;
    const short = expected - received;
    // Short by no more than a card fee, or over by a rounding cent or two.
    return short >= -100 && short <= feeTolerance(expected);
  });
}

export type SetAsideVerdict =
  { setAside: false; why: string } | { setAside: true; why: string };

/**
 * What to do with a deposit that has just landed.
 *
 * Never an error and never a throw: an unknown payer is simply one a person
 * looks at, which is what happened to every deposit before this existed.
 */
export function setAsideVerdict(input: {
  counterparty: string | null | undefined;
  amount: Cents;
  rules: readonly DepositRule[];
  /** Every amount some job is still waiting for. */
  awaited: readonly Cents[];
}): SetAsideVerdict {
  const rule = ruleFor(input.counterparty, input.rules);

  if (!ruleIsEarned(rule)) {
    return { setAside: false, why: "Nobody has set this payer aside before." };
  }

  if (looksLikeAPayment(input.amount, input.awaited)) {
    return {
      setAside: false,
      why:
        `${formatMoney(input.amount)} is what a job is waiting for, so this ` +
        "one still needs a person even though the payer is a familiar one.",
    };
  }

  return {
    setAside: true,
    why: rule!.reason,
  };
}

/**
 * What a payer's count drops to when somebody puts one of its set-asides back.
 *
 * Putting a deposit back is a person saying "that one was real". If the count
 * survives it, the rule that set it aside goes on setting the next one aside
 * too, and the only recovery is to find the rule in Tools and forget it —
 * which nobody looking at the Money In panel knows exists. The whole design
 * rests on setting aside being the recoverable direction (ADR 0098), and it is
 * only recoverable if undoing it un-learns the step that taught it.
 *
 * One step, not the whole rule. A payer set aside three times and put back
 * once is still a habit; a payer that had only just earned its rule drops
 * below the threshold and starts asking again, which is the right answer to
 * somebody disagreeing with it the first time it fired. The reason a person
 * typed is kept either way.
 */
export function timesAfterUndo(timesSetAside: number): number {
  return Math.max(0, Math.trunc(timesSetAside) - 1);
}

/**
 * What to tell somebody who has just set a deposit aside.
 *
 * Null when there is nothing worth saying. The one that matters is the moment
 * the rule is earned, because that is when the behaviour of the screen changes
 * and nobody asked for it to.
 */
export function learningNotice(input: {
  counterpartyLabel: string;
  timesSetAsideNow: number;
}): string | null {
  if (input.timesSetAsideNow < RULE_EARNED_AFTER) {
    return null;
  }
  if (input.timesSetAsideNow === RULE_EARNED_AFTER) {
    return (
      `Set aside. Roof Board will stop asking about ${input.counterpartyLabel} ` +
      "from now on — unless the amount is one a job is waiting for."
    );
  }
  return "Set aside.";
}
